import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrendingPanel = () => {
  const navigate = useNavigate();

  const trendingClaims = [
    {
      id: 1,
      title: "EU Climate Policy Impact Claims",
      trend: "rising",
      searches: 2847,
      verdict: "mixed",
      category: "climate"
    },
    {
      id: 2,
      title: "Healthcare System Statistics",
      trend: "stable",
      searches: 1923,
      verdict: "mostly-true",
      category: "health"
    },
    {
      id: 3,
      title: "Economic Recovery Data",
      trend: "rising",
      searches: 1654,
      verdict: "true",
      category: "economy"
    },
    {
      id: 4,
      title: "Immigration Policy Claims",
      trend: "falling",
      searches: 1432,
      verdict: "false",
      category: "politics"
    },
    {
      id: 5,
      title: "Technology Privacy Concerns",
      trend: "rising",
      searches: 1287,
      verdict: "unverified",
      category: "technology"
    }
  ];

  const recentNarratives = [
    {
      id: 1,
      title: "Cross-border Misinformation Campaign",
      countries: ["DE", "FR", "IT"],
      status: "active",
      severity: "high"
    },
    {
      id: 2,
      title: "Health Policy Coordination",
      countries: ["NL", "BE", "DK"],
      status: "monitoring",
      severity: "medium"
    },
    {
      id: 3,
      title: "Economic Data Verification",
      countries: ["ES", "PT", "FR"],
      status: "resolved",
      severity: "low"
    }
  ];

  const quickExplainers = [
    {
      id: 1,
      title: "How to Verify Social Media Claims",
      type: "guide",
      readTime: "3 min",
      icon: "BookOpen"
    },
    {
      id: 2,
      title: "Understanding Confidence Scores",
      type: "explainer",
      readTime: "2 min",
      icon: "BarChart3"
    },
    {
      id: 3,
      title: "EFCSN Verification Process",
      type: "methodology",
      readTime: "5 min",
      icon: "Shield"
    }
  ];

  const getTrendIcon = (trend) => {
    const icons = {
      'rising': 'TrendingUp',
      'falling': 'TrendingDown',
      'stable': 'Minus'
    };
    return icons?.[trend] || 'Minus';
  };

  const getTrendColor = (trend) => {
    const colors = {
      'rising': 'text-success',
      'falling': 'text-destructive',
      'stable': 'text-muted-foreground'
    };
    return colors?.[trend] || 'text-muted-foreground';
  };

  const getVerdictColor = (verdict) => {
    const colors = {
      'true': 'bg-success/10 text-success',
      'mostly-true': 'bg-success/10 text-success',
      'mixed': 'bg-warning/10 text-warning',
      'mostly-false': 'bg-destructive/10 text-destructive',
      'false': 'bg-destructive/10 text-destructive',
      'unverified': 'bg-muted text-muted-foreground'
    };
    return colors?.[verdict] || colors?.unverified;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'high': 'bg-destructive/10 text-destructive',
      'medium': 'bg-warning/10 text-warning',
      'low': 'bg-success/10 text-success'
    };
    return colors?.[severity] || colors?.low;
  };

  return (
    <div className="space-y-6">
      {/* Trending Claims */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Trending Claims</h3>
          <Button
            variant="ghost"
            size="sm"
            iconName="ExternalLink"
            onClick={() => navigate('/narrative-explorer')}
          >
            View All
          </Button>
        </div>

        <div className="space-y-3">
          {trendingClaims?.map((claim) => (
            <div
              key={claim?.id}
              className="flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg cursor-pointer transition-colors"
              onClick={() => navigate('/claim-detail-analysis', { state: { claimId: claim?.id } })}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {claim?.title}
                </p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-xs text-muted-foreground">
                    {claim?.searches?.toLocaleString()} searches
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getVerdictColor(claim?.verdict)}`}>
                    {claim?.verdict?.replace('-', ' ')}
                  </span>
                </div>
              </div>
              <Icon
                name={getTrendIcon(claim?.trend)}
                size={16}
                className={getTrendColor(claim?.trend)}
              />
            </div>
          ))}
        </div>
      </div>
      {/* Recent Narratives */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Recent Narratives</h3>
          <Button
            variant="ghost"
            size="sm"
            iconName="ExternalLink"
            onClick={() => navigate('/narrative-explorer')}
          >
            Explore
          </Button>
        </div>

        <div className="space-y-3">
          {recentNarratives?.map((narrative) => (
            <div
              key={narrative?.id}
              className="p-3 hover:bg-muted/50 rounded-lg cursor-pointer transition-colors"
              onClick={() => navigate('/narrative-explorer', { state: { narrativeId: narrative?.id } })}
            >
              <div className="flex items-start justify-between mb-2">
                <p className="text-sm font-medium text-foreground">
                  {narrative?.title}
                </p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${getSeverityColor(narrative?.severity)}`}>
                  {narrative?.severity}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-1">
                  {narrative?.countries?.map((country, index) => (
                    <span
                      key={index}
                      className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded"
                    >
                      {country}
                    </span>
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">
                  • {narrative?.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Quick Explainers */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Quick Explainers</h3>
          <Button
            variant="ghost"
            size="sm"
            iconName="BookOpen"
          >
            Library
          </Button>
        </div>

        <div className="space-y-3">
          {quickExplainers?.map((explainer) => (
            <div
              key={explainer?.id}
              className="flex items-center space-x-3 p-3 hover:bg-muted/50 rounded-lg cursor-pointer transition-colors"
            >
              <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
                <Icon name={explainer?.icon} size={16} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {explainer?.title}
                </p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-xs text-muted-foreground">
                    {explainer?.type}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    • {explainer?.readTime}
                  </span>
                </div>
              </div>
              <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TrendingPanel;